"use client";

import { useEffect } from "react";
import * as Sentry from "@sentry/nextjs";
import { Button } from "@aluume/shared/components/ui/Button";

// Route-segment error boundary. Next renders this (instead of a blank/dead page)
// whenever a page or its data throws during render. Gives the user a clear
// message + a way to recover, rather than a silently frozen screen.
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    Sentry.captureException(error);
    console.error("Page error:", error);
  }, [error]);

  return (
    <main className="mx-auto flex max-w-sm flex-1 flex-col justify-center px-6 py-20 text-center">
      <p className="eyebrow mb-3">Aluume</p>
      <h3 className="mb-3">Something went wrong</h3>
      <p className="caption text-ink-mute mb-8">
        This page hit an error and couldn’t load. Try again. If it keeps happening,
        reload the page or let us know.
      </p>
      <div className="flex flex-col gap-3">
        <Button onClick={() => reset()}>Try again</Button>
        <button className="caption underline" onClick={() => window.location.reload()}>
          Reload the page
        </button>
      </div>
      {process.env.NODE_ENV !== "production" && (
        <p className="caption text-corten mt-6 break-words">
          {error.message}
          {error.digest ? ` · ${error.digest}` : ""}
        </p>
      )}
    </main>
  );
}
